import { IDecisionVoucherV2, createEmptyDecisionVoucherV2 } from '../models/decisionVoucherV2';

/**
 * Génère un ID unique pour le localStorage
 * Remplace uuid qui n'est pas disponible
 */
function generateUniqueId(): string {
  return 'v2_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

/**
 * Préfixe pour les clés de stockage localStorage
 */
const STORAGE_PREFIX = 'savVoucherV2_';

/**
 * Clé pour l'index des bons décision (pour recherche rapide)
 */
const INDEX_KEY = `${STORAGE_PREFIX}index`;

/**
 * Interface pour l'index des bons de décision stockés
 */
interface IVoucherIndex {
  [savId: number]: {
    voucherId: string;
    createdAt: string;
    updatedAt: string;
    type: string;
    status: string;
  }[];
}

/**
 * Classe service pour gérer le stockage local des bons de décision
 */
export class DecisionVoucherV2StorageService {
  /**
   * Récupère l'index des bons de décision
   */
  private static getIndex(): IVoucherIndex {
    const indexJson = localStorage.getItem(INDEX_KEY);
    return indexJson ? JSON.parse(indexJson) : {};
  }

  /**
   * Sauvegarde l'index des bons de décision
   */
  private static saveIndex(index: IVoucherIndex): void {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  }

  /**
   * Construit la clé de stockage pour un bon de décision
   */
  private static getVoucherKey(voucherId: string): string {
    return `${STORAGE_PREFIX}voucher_${voucherId}`;
  }

  /**
   * Sauvegarde un bon de décision dans le localStorage
   */
  public static saveVoucher(voucher: IDecisionVoucherV2): IDecisionVoucherV2 {
    // Générer un ID unique si nécessaire
    if (!voucher.id) {
      voucher.id = generateUniqueId();
    }

    // S'assurer que l'ID est défini
    const voucherId = voucher.id as string;

    // Mettre à jour la date de modification
    voucher.updatedAt = new Date().toISOString();

    // Sauvegarder le bon
    const voucherKey = this.getVoucherKey(voucherId);
    localStorage.setItem(voucherKey, JSON.stringify(voucher));

    // Mettre à jour l'index
    const index = this.getIndex();
    if (!index[voucher.savId]) {
      index[voucher.savId] = [];
    }

    // Vérifier si le voucher existe déjà dans l'index et le mettre à jour
    const existingVoucherIndex = index[voucher.savId].findIndex(v => v.voucherId === voucherId);
    if (existingVoucherIndex !== -1) {
      index[voucher.savId][existingVoucherIndex] = {
        voucherId: voucherId,
        createdAt: voucher.createdAt,
        updatedAt: voucher.updatedAt,
        type: voucher.type,
        status: voucher.status,
      };
    } else {
      // Ajouter à l'index
      index[voucher.savId].push({
        voucherId: voucherId,
        createdAt: voucher.createdAt,
        updatedAt: voucher.updatedAt,
        type: voucher.type,
        status: voucher.status,
      });
    }

    this.saveIndex(index);
    return voucher;
  }

  /**
   * Récupère un bon de décision par son ID
   */
  public static getVoucherById(voucherId: string): IDecisionVoucherV2 | null {
    const voucherKey = this.getVoucherKey(voucherId);
    const voucherJson = localStorage.getItem(voucherKey);
    return voucherJson ? JSON.parse(voucherJson) : null;
  }

  /**
   * Récupère le dernier brouillon pour un SAV donné
   */
  public static getLatestDraftForSav(savId: number): IDecisionVoucherV2 | null {
    const index = this.getIndex();
    if (!index[savId] || index[savId].length === 0) {
      return null;
    }

    // Trouver le brouillon le plus récent
    const drafts = index[savId]
      .filter(item => item.status === 'DRAFT')
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

    if (drafts.length === 0) {
      return null;
    }

    return this.getVoucherById(drafts[0].voucherId);
  }

  /**
   * Récupère tous les bons de décision pour un SAV donné
   */
  public static getAllVouchersForSav(savId: number): IDecisionVoucherV2[] {
    const index = this.getIndex();
    if (!index[savId] || index[savId].length === 0) {
      return [];
    }

    return index[savId]
      .map(item => this.getVoucherById(item.voucherId))
      .filter(voucher => voucher !== null) as IDecisionVoucherV2[];
  }

  /**
   * Crée un nouveau brouillon pour un SAV
   */
  public static createNewDraft(savId: number): IDecisionVoucherV2 {
    const voucher = createEmptyDecisionVoucherV2(savId);
    return this.saveVoucher(voucher);
  }

  /**
   * Supprime un bon de décision par son ID
   */
  public static deleteVoucher(voucherId: string): boolean {
    const voucher = this.getVoucherById(voucherId);
    if (!voucher) {
      return false;
    }

    // Supprimer du localStorage
    const voucherKey = this.getVoucherKey(voucherId);
    localStorage.removeItem(voucherKey);

    // Mettre à jour l'index
    const index = this.getIndex();
    if (index[voucher.savId]) {
      index[voucher.savId] = index[voucher.savId].filter(item => item.voucherId !== voucherId);
      this.saveIndex(index);
    }

    return true;
  }

  /**
   * Crée une copie/version d'un bon existant
   */
  public static createVersionFromVoucher(voucherId: string): IDecisionVoucherV2 | null {
    const sourceVoucher = this.getVoucherById(voucherId);
    if (!sourceVoucher) {
      return null;
    }

    const now = new Date().toISOString();
    const newVersion: IDecisionVoucherV2 = {
      ...sourceVoucher,
      id: undefined, // Sera généré lors de la sauvegarde
      parentId: sourceVoucher.id,
      createdAt: now,
      updatedAt: now,
      version: sourceVoucher.version + 1,
    };

    return this.saveVoucher(newVersion);
  }

  /**
   * Importe un bon de décision (utile pour tests et migrations)
   */
  public static importVoucher(voucher: IDecisionVoucherV2): IDecisionVoucherV2 {
    return this.saveVoucher(voucher);
  }

  /**
   * Exporte tous les bons de décision (utile pour tests et migrations)
   */
  public static exportAllVouchers(): IDecisionVoucherV2[] {
    const index = this.getIndex();
    const allVouchers: IDecisionVoucherV2[] = [];

    Object.keys(index).forEach(savIdStr => {
      const savId = parseInt(savIdStr, 10);
      const vouchers = this.getAllVouchersForSav(savId);
      allVouchers.push(...vouchers);
    });

    return allVouchers;
  }

  /**
   * Nettoie les bons de décision plus vieux qu'une certaine date
   */
  public static cleanupOldDrafts(olderThan: Date): number {
    const index = this.getIndex();
    let deletedCount = 0;

    Object.keys(index).forEach(savIdStr => {
      const savId = parseInt(savIdStr, 10);
      const vouchersToKeep: typeof index[number] = [];

      index[savId].forEach(voucherIndex => {
        const voucher = this.getVoucherById(voucherIndex.voucherId);
        if (
          voucher &&
          (voucher.status !== 'DRAFT' ||
           new Date(voucher.updatedAt) >= olderThan)
        ) {
          vouchersToKeep.push(voucherIndex);
        } else {
          // Supprimer le voucher
          if (voucher) {
            const voucherKey = this.getVoucherKey(voucherIndex.voucherId);
            localStorage.removeItem(voucherKey);
            deletedCount++;
          }
        }
      });

      index[savId] = vouchersToKeep;
    });

    this.saveIndex(index);
    return deletedCount;
  }

  /**
   * Supprime tous les bons de décision (attention, destructif!)
   */
  public static clearAll(): void {
    const index = this.getIndex();

    // Supprimer tous les vouchers
    Object.values(index).flat().forEach(voucherIndex => {
      const voucherKey = this.getVoucherKey(voucherIndex.voucherId);
      localStorage.removeItem(voucherKey);
    });

    // Supprimer l'index
    localStorage.removeItem(INDEX_KEY);
  }

  /**
   * Convertit un ancien modèle IVoucher vers le nouveau modèle V2 (pour migration future)
   */
  public static convertLegacyVoucher(_legacyVoucher: any, savId: number): IDecisionVoucherV2 {
    // Cette méthode sera implémentée ultérieurement pour la migration
    // des données existantes vers le nouveau format
    // const now = new Date().toISOString();

    // Créer un voucher vide
    const voucher = createEmptyDecisionVoucherV2(savId);

    // Le reste de l'implémentation dépendra de la structure exacte de l'ancien modèle
    // et sera complété lors de la phase de migration

    return voucher;
  }
}